/**
 * account-plan-store.ts
 *
 * Supabase-backed persistence for AccountPlan records, with a localStorage
 * write-through cache for instant first-paint and offline reads.
 *
 * Source of truth: public.account_plans (one row per user_id + account_id).
 * The full AccountPlan blob is persisted in account_plans.plan_data jsonb.
 *
 * Cache key (unchanged from the legacy localStorage-only era):
 *   aip:account-plan:{account_id}
 *
 * On read: Supabase first; fall back to cache on auth or network failure.
 * On write: cache first (instant UI), then Supabase upsert. A failed Supabase
 *          write logs to console.error and surfaces via toast (if a handler
 *          was wired by the app shell). The local cache still holds the row
 *          so a later session can re-attempt sync.
 *
 * Brand: Navy #0B0B0F, Red #DC2626, Attilius bg #FAFAF7
 * Roles: bdr, ae, manager, executive, marketing — never "rep"
 * M-PACT always spelled with a hyphen.
 */

import { supabase } from "@/lib/session";
import type { AccountPlan, AuditEntry } from "@/types/account-plan";
import { blankMpact, defaultDisqualification } from "@/types/account-plan";
import { logActivity } from "./activity-log";

const KEY_PREFIX = "aip:account-plan:";
const TABLE_PLANS = "account_plans";

export function planKey(accountId: string): string {
  return `${KEY_PREFIX}${accountId}`;
}

// Toast wiring — no hard sonner dependency; app shell wires at startup.
type ToastFn = (message: string) => void;
let externalToast: ToastFn | null = null;
export function setToastHandler(fn: ToastFn | null): void {
  externalToast = fn;
}
function surface(message: string, err: unknown): void {
  console.error(`[account-plan-store] ${message}`, err);
  try {
    if (externalToast) externalToast(message);
  } catch {
    // never let a toast handler error break a write path
  }
}

// localStorage cache helpers

function readCache(accountId: string): AccountPlan | null {
  try {
    const raw = localStorage.getItem(planKey(accountId));
    return raw ? (JSON.parse(raw) as AccountPlan) : null;
  } catch {
    return null;
  }
}

function writeCache(plan: AccountPlan): void {
  try {
    localStorage.setItem(planKey(plan.account_id), JSON.stringify(plan));
  } catch {
    // quota exceeded or private mode — Supabase remains source of truth
  }
}

// Row mapping

interface AccountPlanRow {
  id: string;
  tenant_id: number;
  user_id: string;
  account_name: string;
  account_id: string | null;
  vertical: string | null;
  status: string | null;
  priority: number | null;
  plan_data: Record<string, unknown> | null;
  composite_score: number | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
}

function rowToPlan(row: AccountPlanRow): AccountPlan {
  const blob = (row.plan_data ?? {}) as Partial<AccountPlan>;
  return {
    id: (blob.id as string) ?? row.id,
    account_id: row.account_id ?? (blob.account_id as string) ?? row.account_name,
    account_name: row.account_name ?? (blob.account_name as string) ?? "",
    industry: (blob.industry as string) ?? row.vertical ?? "",
    icp_confirmed: (blob.icp_confirmed as boolean) ?? false,
    tenant_id: row.tenant_id ?? 1,
    owner_id: (blob.owner_id as string) ?? row.user_id,
    bdr_id: (blob.bdr_id as string) ?? "",
    bootcamp_seed: (blob.bootcamp_seed as AccountPlan["bootcamp_seed"]) ?? {
      why_this_account: "",
      initial_pain_hypothesis: "",
      relationship_map: [],
      source_of_awareness: "",
      competitive_intel: "",
      historical_attempts: "",
      first_move: "",
      target_personas: "",
    },
    mpact: (blob.mpact as AccountPlan["mpact"]) ?? blankMpact(),
    opportunities: (blob.opportunities as AccountPlan["opportunities"]) ?? [],
    next_best_play: (blob.next_best_play as AccountPlan["next_best_play"]) ?? {
      play_type: "discover",
      description: "",
      revenue_impact_estimate: 0,
      suggested_owner: "",
      suggested_date: "",
      auto_ranked: true,
      rank_score: 0,
    },
    disqualification:
      (blob.disqualification as AccountPlan["disqualification"]) ??
      defaultDisqualification(),
    audit_log: (blob.audit_log as AuditEntry[]) ?? [],
    created_at: row.created_at,
    updated_at: row.updated_at,
    last_activity_at: row.last_activity_at ?? "",
    rank_in_top10: (blob.rank_in_top10 as number) ?? 0,
  };
}

function planToRow(plan: AccountPlan, userId: string) {
  return {
    tenant_id: plan.tenant_id || 1,
    user_id: userId,
    account_name: plan.account_name || plan.account_id,
    account_id: plan.account_id || null,
    // Supabase account_plans_vertical_check currently rejects all non-null
    // values. The industry stays inside plan_data; the top-level column is
    // null until the schema constraint is updated to accept the controlled list.
    vertical: null,
    status: "active" as const,
    priority: plan.rank_in_top10 > 0 ? Math.min(plan.rank_in_top10, 10) : null,
    plan_data: plan as unknown as Record<string, unknown>,
    last_activity_at: plan.last_activity_at || new Date().toISOString(),
  };
}

// Public API

/**
 * Load the AccountPlan for the given account. Supabase first; falls back to
 * the localStorage cache when no session, no network, or no remote row.
 * Returns null if nothing is found in either place.
 */
export async function loadPlan(accountId: string): Promise<AccountPlan | null> {
  try {
    const sb = supabase();
    const { data: authData, error: authErr } = await sb.auth.getUser();
    const user = authData?.user;
    if (authErr || !user) {
      return readCache(accountId);
    }
    const { data, error } = await sb
      .from(TABLE_PLANS)
      .select("*")
      .eq("user_id", user.id)
      .eq("account_id", accountId)
      .order("updated_at", { ascending: false })
      .limit(1);
    if (error) {
      surface("Could not load plan from server — using local copy", error);
      return readCache(accountId);
    }
    if (data && data.length > 0) {
      const plan = rowToPlan(data[0] as AccountPlanRow);
      writeCache(plan);
      return plan;
    }
    return readCache(accountId);
  } catch (err) {
    surface("Plan load failed — using local copy", err);
    return readCache(accountId);
  }
}

/**
 * Persist the plan. Cache first (instant), then Supabase upsert. Returns once
 * the network round-trip resolves. On Supabase failure: cache still holds the
 * row, console.error fires, toast surfaces if a handler was wired.
 */
export async function savePlan(plan: AccountPlan): Promise<void> {
  plan.updated_at = new Date().toISOString();
  writeCache(plan);

  try {
    const sb = supabase();
    const { data: authData, error: authErr } = await sb.auth.getUser();
    const user = authData?.user;
    if (authErr || !user) {
      surface("Saved locally — sign in to sync to server", authErr ?? "no session");
      return;
    }
    const row = planToRow(plan, user.id);
    // Upsert on the existing UNIQUE (tenant_id, account_id) constraint. Plans
    // are shared per tenant + account; the most recent writer wins. RLS still
    // enforces tenant scope. The select-then-write pattern that lived here
    // previously caused races and "server insert failed" toasts when two users
    // on the same tenant both passed the existence check.
    const { error: upsertErr } = await sb
      .from(TABLE_PLANS)
      .upsert(row, { onConflict: "tenant_id,account_id" });
    if (upsertErr) {
      surface("Plan saved locally but server upsert failed", upsertErr);
      return;
    }
  } catch (err) {
    surface("Plan saved locally but server upsert threw", err);
    return;
  }

  // Fire-and-forget audit feed write — never blocks the save.
  void logActivity({
    action: "plan.save",
    entity_type: "account_plan",
    entity_id: plan.id,
    payload: {
      account_name: plan.account_name,
      industry: plan.industry,
      status: (plan as any).status ?? null,
      priority: (plan as any).priority ?? null,
    },
  });
}

/** Append an audit entry and persist. */
export async function appendAudit(
  plan: AccountPlan,
  entry: Omit<AuditEntry, "id" | "changed_at">,
): Promise<AccountPlan> {
  const updated: AccountPlan = {
    ...plan,
    audit_log: [
      ...plan.audit_log,
      {
        ...entry,
        id: crypto.randomUUID(),
        changed_at: new Date().toISOString(),
      },
    ],
    updated_at: new Date().toISOString(),
    last_activity_at: new Date().toISOString(),
  };
  await savePlan(updated);
  void logActivity({
    action: entry.field === "status" ? "plan.status_change" : "mpact.answer_save",
    entity_type: "account_plan",
    entity_id: updated.id,
    payload: {
      field: entry.field,
      old_value: (entry as any).old_value ?? null,
      new_value: (entry as any).new_value ?? null,
      account_name: updated.account_name,
    },
  });
  return updated;
}

/** Create a blank plan for an account that has no seed data. */
export function blankPlan(accountId: string, accountName: string, industry: string): AccountPlan {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    account_id: accountId,
    account_name: accountName,
    industry,
    icp_confirmed: false,
    tenant_id: 1,
    owner_id: "",
    bdr_id: "",
    bootcamp_seed: {
      why_this_account: "",
      initial_pain_hypothesis: "",
      relationship_map: [],
      source_of_awareness: "",
      competitive_intel: "",
      historical_attempts: "",
      first_move: "",
      target_personas: "",
    },
    mpact: blankMpact(),
    opportunities: [],
    next_best_play: {
      play_type: "discover",
      description: "Complete M-PACT discovery for CP1-CP3 before advancing to budget conversation.",
      revenue_impact_estimate: 0,
      suggested_owner: "",
      suggested_date: "",
      auto_ranked: true,
      rank_score: 0,
    },
    disqualification: defaultDisqualification(),
    audit_log: [],
    created_at: now,
    updated_at: now,
    last_activity_at: "",
    rank_in_top10: 0,
  };
}

/** Days since last activity. Returns null when no activity has ever been logged. */
export function daysSinceActivity(plan: AccountPlan): number | null {
  if (!plan.last_activity_at) return null;
  const last = new Date(plan.last_activity_at).getTime();
  if (!Number.isFinite(last)) return null;
  return Math.floor((Date.now() - last) / 86_400_000);
}

/** Human-readable relative time. */
export function relTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}
